import { t, Trans } from '@lingui/macro'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { CircularProgress, IconButton, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { DataGrid, GridColDef, GridRowSpacingParams } from '@mui/x-data-grid'
import { InsightsReportDTO } from '@om1/falcon-api'
import { useUserPermissions } from '@om1/platform-authentication'
import { FrameworkButton } from '@om1/platform-components'
import { getStyledGrid } from '@om1/platform-ui-kit/src/styles'
import { PlatformPermissions, RoutedFrameworkComponentProps } from '@om1/platform-utils'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { rwaInsightsReportActions, RWAInsightsReportsState } from '../state/rwa-insights-reports'
import { createAddRWAInsightsReportsModal } from './AddRWAInsightsReportModal'
import { DeleteRWAInsightsReportDialog } from './DeleteRWAInsightsReportDialog'

const AddRWAInsightsReportsModal = createAddRWAInsightsReportsModal()

export type RWAInsightsReportsPageComponentProps = RoutedFrameworkComponentProps<
    {},
    {},
    { rwaInsightsReports: RWAInsightsReportsState },
    typeof rwaInsightsReportActions,
    {}
>

export const RWAInsightsReportsPageComponent: FunctionComponent<RWAInsightsReportsPageComponentProps> = ({ state, actions }) => {
    const { permissions } = useUserPermissions()
    const [addRWAInsightsReportsModalVisible, setAddRWAInsightsReportsModalVisible] = useState(false)

    const handleAddRWAInsightsReportsModalOpen = () => {
        setAddRWAInsightsReportsModalVisible(true)
    }

    const handleAddRWAInsightsReportsModalClose = () => {
        setAddRWAInsightsReportsModalVisible(false)
    }

    const StyledDataGrid = getStyledGrid(DataGrid) as typeof DataGrid

    const [deleteModalInsightReport, setDeleteModalInsightReport] = useState<InsightsReportDTO>()
    const handleDeleteHide = useCallback(() => setDeleteModalInsightReport(undefined), [])

    const columns = useMemo<GridColDef<InsightsReportDTO>[]>(() => {
        const columns = [
            {
                field: 'name',
                headerName: t`Name`,
                renderCell: (params) => (
                    <Box display='flex' flexDirection='column' gap={1} minWidth={0} maxWidth='100%'>
                        <Typography color='primary' fontWeight='bold' className='grid-content' noWrap>
                            <>{params.row.name}</>
                        </Typography>
                    </Box>
                ),
                flex: 3,
                sortable: false,
                filterable: false
            },
            {
                field: 'description',
                headerName: t`Description`,
                renderCell: (params) => (
                    <Box display='flex' flexDirection='column' gap={1} minWidth={0} maxWidth='100%'>
                        <Typography color='primary' fontWeight='bold' className='grid-content' noWrap>
                            <>{params.row.description}</>
                        </Typography>
                    </Box>
                ),
                flex: 3,
                sortable: false,
                filterable: false
            },
            {
                field: 'powerbiReportId',
                headerName: t`PowerBI Report ID`,
                renderCell: (params) => (
                    <Box display='flex' flexDirection='column' gap={1} minWidth={0} maxWidth='100%'>
                        <Typography color='primary' fontWeight='bold' className='grid-content' noWrap>
                            <>{params.row.powerbiReportId}</>
                        </Typography>
                    </Box>
                ),
                flex: 3,
                sortable: false,
                filterable: false
            },
            {
                field: 'powerbiDatasetId',
                headerName: t`PowerBI Dataset ID`,
                renderCell: (params) => (
                    <Box display='flex' flexDirection='column' gap={1} minWidth={0} maxWidth='100%'>
                        <Typography color='primary' fontWeight='bold' className='grid-content' noWrap>
                            <>{params.row.powerbiDatasetId}</>
                        </Typography>
                    </Box>
                ),
                flex: 3,
                sortable: false,
                filterable: false
            },
            {
                field: 'powerbiWorkspaceId',
                headerName: t`PowerBI Workspace ID`,
                renderCell: (params) => (
                    <Box display='flex' flexDirection='column' gap={1} minWidth={0} maxWidth='100%'>
                        <Typography color='primary' fontWeight='bold' className='grid-content' noWrap>
                            <>{params.row.powerbiWorkspaceId}</>
                        </Typography>
                    </Box>
                ),
                flex: 3,
                sortable: false,
                filterable: false
            }
        ]
        if (permissions?.includes(PlatformPermissions.DELETE_RWA_INSIGHTS_REPORTS)) {
            columns.push({
                field: 'delete',
                headerName: t`Delete`,
                renderCell: (params) => (
                    <Box
                        display='flex'
                        flexDirection='column'
                        gap={1}
                        minWidth={0}
                        maxWidth='100%'
                        paddingTop={0}
                        paddingBottom={0}
                        sx={{ border: 'none' }}
                    >
                        <IconButton
                            key='delete-InsightsReports'
                            color='primary'
                            aria-label={t`Delete`}
                            sx={{ backgroundColor: '#D6E4F2', color: '#012D72', borderRadius: '2px' }}
                            onClick={() => setDeleteModalInsightReport(params.row)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ),
                flex: 3,
                sortable: false,
                filterable: false
            })
        }
        return columns
    }, [permissions])

    if (state.rwaInsightsReports.loading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' flex='1'>
                <CircularProgress />
            </Box>
        )
    } else {
        return (
            <>
                <AddRWAInsightsReportsModal
                    showAddRWAInsightsReportsModal={addRWAInsightsReportsModalVisible}
                    closeModal={handleAddRWAInsightsReportsModalClose}
                />
                <Box display='flex' justifyContent='space-between' alignItems='center' flex='1'>
                    <h1>
                        <Trans>RWA Insights Reports Page</Trans>
                    </h1>
                    {permissions?.includes(PlatformPermissions.CREATE_RWA_INSIGHTS_REPORTS) && (
                        <FrameworkButton
                            variant='contained'
                            color='primary'
                            onClick={handleAddRWAInsightsReportsModalOpen}
                            sx={{ width: '250px' }}
                            aria-label={t`Create Cohort`}
                        >
                            <AddIcon sx={{ height: '18.5px', width: '18.5px', margin: '0.1rem', marginRight: '10px' }} />
                            <Trans>Create Insights Report</Trans>
                        </FrameworkButton>
                    )}
                </Box>
                {state.rwaInsightsReports.insightReports.length === 0 ? (
                    <Trans>No Insights Reports</Trans>
                ) : (
                    <Box>
                        <StyledDataGrid
                            autoHeight
                            disableColumnMenu
                            disableRowSelectionOnClick
                            rowHeight={80}
                            getRowSpacing={(params: GridRowSpacingParams) => {
                                return {
                                    top: params.isFirstVisible ? 0 : 4,
                                    bottom: params.isLastVisible ? 0 : 4
                                }
                            }}
                            columns={columns}
                            hideFooter={true}
                            rows={state.rwaInsightsReports.insightReports}
                        />
                    </Box>
                )}
                {deleteModalInsightReport && (
                    <DeleteRWAInsightsReportDialog id={deleteModalInsightReport.id} actions={actions} onCancel={handleDeleteHide} />
                )}
            </>
        )
    }
}
